import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography, // Για την κεφαλίδα του πίνακα
} from '@mui/material';
import ChooseSignerModal from '../components/searchUnpaid/ChooseSignerModal';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';

export default function SearchManualDebts() {
  const [year, setYear] = useState('');
  const [debt, setDebt] = useState(null);
  const [dataSubmitted, setDataSubmitted] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [enableSearch, setEnableSearch] = useState(false);

  // ΝΕΟ STATE ΓΙΑ ΤΑ ΔΕΔΟΜΕΝΑ ΤΟΥ ΠΙΝΑΚΑ
  const [unpaidDebts, setUnpaidDebts] = useState([]);

  const { loading, setLoading, notification, setNotification, setLoggedInUser } =
      useAuthContext();
  const navigate = useNavigate();

  const [openSignerModal, setOpenSignerModal] = useState(false);
  const [signer, setSigner] = useState({ id: null, value: '' });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/getListOfYears`,
          {
            withCredentials: true,
          },
        );

        setLoading(false);
        setResponseData(response.data);
      } catch (error) {
        setLoading(false);
        setNotification({
          message: error.response?.data?.message || 'Σφάλμα φόρτωσης ετών.',
          open: true,
          color: 'error',
        });
        if (error.response?.status === 401) {
          setLoggedInUser(null);
          navigate('/login');
        }
        console.error(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (year !== '') {
      setEnableSearch(true);
    } else {
      setEnableSearch(false);
    }
  }, [year]);

  const clearStates = () => {
    setDataSubmitted(null);
    setYear('');
    setDebt(null);
    setUnpaidDebts([]);
  };

  const handleSearch = async () => {
    setLoading(true);
    setUnpaidDebts([]); // Καθαρισμός προηγούμενων αποτελεσμάτων

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/getUnpaidSubscriptionsRows`,
        { year },
        { withCredentials: true },
      );

      setLoading(false);
      setDataSubmitted({ year });

      // ΥΠΟΘΕΣΗ: Το backend επιστρέφει τα δεδομένα του πίνακα στο response.data.debtList
      // Εάν η δομή είναι διαφορετική, προσαρμόστε το.
      const debtList = response.data.debtList || [];
      setUnpaidDebts(debtList);

      setDebt({
        sum: response.data.debt?.toLocaleString(),
        amount: response.data.amount
          ? response.data.amount.toLocaleString()
          : 0,
      });

    } catch (error) {
      setLoading(false);
      setNotification({
        message: error.response?.data?.message || 'Αποτυχία αναζήτησης.',
        open: true,
        color: 'error',
      });
      if (error.response?.status === 401) {
        setLoggedInUser(null);
        navigate('/login');
      }
      console.log(error);
    }
  };

  const handleConfirmation = async () => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/createConfirmations`,
        {
          year: dataSubmitted.year,
          signerId: signer.id,
        },
        { withCredentials: true },
      );

      setLoading(false);
      clearStates();

      const message = `Στάλθηκαν για βεβαίωση ${response.data} εγγραφές`;
      setNotification({ message: message, open: true, color: 'success' });
    } catch (error) {
      setLoading(false);
      setNotification({
        message: error.response?.data?.message || 'Αποτυχία βεβαίωσης οφειλών.',
        open: true,
        color: 'error',
      });

      if (error.response?.status === 401) {
        setLoggedInUser(null);
        navigate('/login');
      }
      console.log(error);
    }
  };

  return (
    <>
      {/* eslint-disable-next-line multiline-ternary */}
      {responseData ? (
        <Box>
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'start' },
              marginTop: '5em',
              marginLeft: { xs: '0em', md: '5em' },
              marginBottom: '5em',
            }}
          >
            <h2>Αναζήτηση και Βεβαίωση Απλήρωτων Οφειλών</h2>
          </Box>

          {/* ---------------------------------- */}
          {/* 1. ΦΟΡΜΑ ΑΝΑΖΗΤΗΣΗΣ & ΑΠΟΤΕΛΕΣΜΑΤΑ ΣΥΝΟΨΗΣ */}
          {/* ---------------------------------- */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              width: '90vw',
              margin: 'auto',
              gap: '1em',
            }}
          >
            <FormControl
              required
              sx={{
                width: { xs: '50%', md: '20%' },
              }}
            >
              <InputLabel id='year-select-label'>Έτος</InputLabel>
              <Select
                value={year}
                label='Έτος'
                fullWidth
                onChange={(event) => {
                  setYear(event.target.value);
                }}
              >
                {responseData.years.map((year, index) => (
                  <MenuItem key={index} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                margin: '1em',
              }}
            >
              <Button
                variant='contained'
                disabled={!enableSearch || loading}
                onClick={handleSearch}
              >
                {loading && !unpaidDebts.length
                  ? <CircularProgress size={24} color='inherit' />
                  : 'Αναζήτηση'}
              </Button>
            </Box>

            {/* Σύνοψη Αποτελεσμάτων */}
            {debt !== null && dataSubmitted && (
              <Box
                className='unpaid-sub-result'
                sx={{
                  flexDirection: 'column',
                }}
              >
                {debt.sum != 0
                  ? (
                    <>
                      <p>
                        Βρέθηκαν
                        <b> {debt.sum}</b> απλήρωτες οφειλές το έτος
                        <b> {dataSubmitted.year}</b> ύψους{' '}
                        <b>{debt.amount}&euro;</b>. Πατήστε Συνέχεια για βεβαίωση.
                      </p>
                      <Button
                        variant='contained'
                        disabled={loading}
                        onClick={() => {
                          setOpenSignerModal(true);
                        }}
                      >
                        {loading
                          ? <CircularProgress size={24} color='inherit' />
                          : 'Συνέχεια'}
                      </Button>
                    </>
                  )
                  : (
                    <p>
                      Δεν βρέθηκαν
                      <> απλήρωτες</> οφειλές που να μήν έχουν οδηγηθεί για
                      βεβαίωση το έτος
                      <b> {dataSubmitted.year}</b>
                    </p>
                  )}
              </Box>
            )}
          </Box>

          {/* ---------------------------------- */}
          {/* 2. ΠΙΝΑΚΑΣ ΔΕΔΟΜΕΝΩΝ ΟΦΕΙΛΩΝ */}
          {/* ---------------------------------- */}
          {unpaidDebts.length > 0 && (
            <Box sx={{ width: '90vw', margin: '3em auto', padding: '1em' }}>
              <Typography variant='h6' gutterBottom>
                Λεπτομερής Λίστα Απλήρωτων Οφειλών ({unpaidDebts.length} εγγραφές)
              </Typography>
              <TableContainer component={Paper} elevation={3}>
                <Table sx={{ minWidth: 650 }} aria-label='unpaid debts table'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                      <TableCell>ΑΦΜ</TableCell>
                      <TableCell>Επωνυμία</TableCell>
                      <TableCell>Είδος Φόρου</TableCell>
                      <TableCell align='right'>Ποσό (€)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unpaidDebts.map((debtItem, index) => (
                      <TableRow
                        key={index}
                        sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}
                      >
                        {/* ΥΠΟΘΕΣΗ: Τα πεδία του backend είναι afm, companyName, taxType, amount */}
                        <TableCell component='th' scope='row'>
                          {debtItem.afm}
                        </TableCell>
                        <TableCell>{debtItem.co_name}</TableCell>
                        <TableCell>{debtItem.txt_code}</TableCell>
                        <TableCell align='right'>
                          {/* Φορμάρισμα ποσού σε νόμισμα */}
                          {new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(debtItem.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      ) : (
        <Box height='100%'>
          {/* Εμφάνιση loading spinner κατά την αρχική φόρτωση των ετών */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
      <ChooseSignerModal
        signer={signer}
        setSigner={setSigner}
        handleConfirmation={handleConfirmation}
        open={openSignerModal}
        setOpen={setOpenSignerModal}
      />
    </>
  );
}
